import { Skeleton, Stack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import _ from 'lodash';
import React, { useMemo } from 'react';

import { utils as commonUtils } from '@finley-technologies/common';

import {
  GetCurrentFundingRequestDocumentGQLType,
  GetFundingRequestDocumentGQLType,
  GetFundingRequestWithBaseDetailsDocumentGQLType,
  useGetUserApprovalsForFundingRequestQuery,
  useUserApprovalActionForFundingRequestWorkflowInstanceMutation,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { colors } from 'packages/common-frontend/colors';
import { Typography } from 'packages/common-frontend/components/Typography';
import { InternalApprovalWorkflowInstance } from 'packages/workflows/ui/WorkflowInstance';

export const FundingRequestInternalApproval = ({
  fundingRequestId,
  workflowInstanceId,
  creditFacilityId,
  creditFacilityName,
  readOnly = false,
}: {
  fundingRequestId: string;
  workflowInstanceId: string;
  creditFacilityId: string;
  creditFacilityName: string;
  readOnly?: boolean;
}) => {
  const currentUserApprovalsForFundingRequest = useGetUserApprovalsForFundingRequestQuery(
    {
      fetchPolicy: 'network-only',
      variables: {
        fundingRequestId,
      },
    },
  );
  if (currentUserApprovalsForFundingRequest.error) {
    Sentry.captureException(currentUserApprovalsForFundingRequest.error);
    throw Error(
      `Failed to get user approvals for funding request. Contact Finley Support.`,
    );
  }
  const currentUserApprovalsByUserId = useMemo(() => {
    if (currentUserApprovalsForFundingRequest.data) {
      const currentUserApprovals =
        currentUserApprovalsForFundingRequest.data
          .getUserApprovalsForFundingRequest;

      return _.keyBy(currentUserApprovals, (approval) => approval.user.id);
    }

    return {};
  }, [currentUserApprovalsForFundingRequest.data]);

  const [
    userApprovalActionForFundingRequestWorkflowInstanceMutation,
    userApprovalActionMutation,
  ] = useUserApprovalActionForFundingRequestWorkflowInstanceMutation({
    fetchPolicy: 'network-only',
    refetchQueries: [
      {
        query: GetFundingRequestWithBaseDetailsDocumentGQLType,
        variables: { fundingRequestId },
        fetchPolicy: 'network-only',
      },
      {
        query: GetFundingRequestDocumentGQLType,
        variables: { fundingRequestId },
        fetchPolicy: 'network-only',
      },
      {
        query: GetCurrentFundingRequestDocumentGQLType,
        variables: { creditFacilityId },
        fetchPolicy: 'network-only',
      },
    ],
  });

  if (userApprovalActionMutation.error) {
    if (commonUtils.EnvUtils.isProduction()) {
      Sentry.captureException(userApprovalActionMutation.error);
      throw Error(
        `Failed to execute user action for funding request. Contact Finley Support.`,
      );
    } else {
      throw userApprovalActionMutation.error;
    }
  }

  if (
    !currentUserApprovalsForFundingRequest.data ||
    currentUserApprovalsForFundingRequest.loading ||
    userApprovalActionMutation.loading
  ) {
    return <Skeleton h="400px" />;
  }

  return (
    <Stack
      w="100%"
      justifyContent="flex-start"
      p="40px"
      border={`1px solid ${colors.gray[200]}`}
      borderRadius="8px"
    >
      <Typography variant="section-title" fontWeight="bold">
        Approve Funding Request
      </Typography>

      <Stack gap="3rem">
        <Typography variant="detail">
          Approval from the following users is required in sequence before a
          Funding Request can be approved.
        </Typography>
        <InternalApprovalWorkflowInstance
          workflowInstanceId={workflowInstanceId}
          readOnly={readOnly}
          currentUserApprovalsForDomain={currentUserApprovalsByUserId}
          approveParams={{
            approvalConfirmationText: `Are you sure you would like to approve this funding request for ${_.startCase(
              creditFacilityName,
            )}?`,
            onUserApprove: async (params: { userId: string }) => {
              if (readOnly) {
                return;
              }
              await userApprovalActionForFundingRequestWorkflowInstanceMutation(
                {
                  variables: {
                    action: 'approve',
                    fundingRequestId,
                    workflowInstanceId,
                    userId: params.userId,
                  },
                },
              );
              await currentUserApprovalsForFundingRequest.refetch();
            },
          }}
          rejectParams={{
            allowMultipleRejections: false,
            rejectConfirmationText: `Are you sure you would like to reject this funding request for ${_.startCase(
              creditFacilityName,
            )}?`,
            onUserReject: async (params: { userId: string }) => {
              if (readOnly) {
                return;
              }
              await userApprovalActionForFundingRequestWorkflowInstanceMutation(
                {
                  variables: {
                    action: 'reject',
                    fundingRequestId,
                    workflowInstanceId,
                    userId: params.userId,
                  },
                },
              );
              await currentUserApprovalsForFundingRequest.refetch();
            },
          }}
        />
      </Stack>
    </Stack>
  );
};
